import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldCheck, Cookie } from "lucide-react";
import { Link } from "react-router-dom";
import { getConsent, saveConsent } from "@/lib/consent";

export default function CookieConsent() {
  const [state, setState] = useState({ visible: false, settings: false, marketing: false });

  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      const t = setTimeout(() => setState((s) => ({ ...s, visible: true })), 900);
      return () => clearTimeout(t);
    }
  }, []);

  const acceptAll = () => { saveConsent({ marketing: true }); setState({ visible: false, settings: false, marketing: true }); };
  const rejectAll = () => { saveConsent({ marketing: false }); setState({ visible: false, settings: false, marketing: false }); };
  const savePrefs = () => { saveConsent({ marketing: state.marketing }); setState((s) => ({ ...s, visible: false, settings: false })); };
  const openSettings = () => setState((s) => ({ ...s, settings: true }));
  const closeSettings = () => setState((s) => ({ ...s, settings: false }));

  return (
    <AnimatePresence>
      {state.visible && (
        <>
          {/* Compact banner (bottom-left) */}
          {!state.settings && (
            <motion.div
              key="banner"
              initial={{ y: 24, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 24, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="fixed z-[100] bottom-5 left-5 right-5 md:right-auto md:max-w-[420px] bg-[#0A0A0A] text-[#FAFAF7] border border-[#C9A961]/40 shadow-[0_30px_80px_-24px_rgba(0,0,0,0.55)] p-6"
              role="dialog"
              aria-label="Cookie-Zustimmung"
              data-testid="cookie-banner"
            >
              <span className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#C9A961] to-transparent" />
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full border border-[#C9A961]/50 flex items-center justify-center flex-shrink-0">
                  <Cookie size={16} className="text-[#C9A961]" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <div className="font-mono-eyebrow text-[#C9A961] mb-2">Cookies & Datenschutz</div>
                  <p className="text-[13.5px] leading-relaxed text-[#FAFAF7]/80">
                    Wir verwenden ausschliesslich technisch notwendige Cookies und — mit Ihrer Zustimmung — Marketing-Cookies (Meta Pixel), um unsere Werbung zu messen. Sie können jederzeit widerrufen.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <button
                      onClick={acceptAll}
                      className="btn-gold !py-2.5 !px-4 text-[12.5px]"
                      data-testid="cookie-accept-all"
                    >
                      Alle akzeptieren
                    </button>
                    <button
                      onClick={rejectAll}
                      className="btn-ghost !py-2.5 !px-4 text-[12.5px] !border-white/20 !text-[#FAFAF7] hover:!bg-white/[0.06]"
                      data-testid="cookie-reject-all"
                    >
                      Ablehnen
                    </button>
                    <button
                      onClick={openSettings}
                      className="text-[12.5px] px-3 py-2.5 text-[#FAFAF7]/70 hover:text-[#C9A961] transition-colors underline underline-offset-4 decoration-white/20 hover:decoration-[#C9A961]"
                      data-testid="cookie-settings-open"
                    >
                      Einstellungen
                    </button>
                  </div>
                  <div className="mt-3 text-[11px] text-[#FAFAF7]/45">
                    Mehr in unserer <Link to="/datenschutz" className="underline hover:text-[#C9A961]">Datenschutzerklärung</Link>.
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Settings modal */}
          {state.settings && (
            <motion.div
              key="modal"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[110] bg-[#0A0A0A]/70 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={closeSettings}
            >
              <motion.div
                initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="w-full max-w-[560px] bg-[#FAFAF7] border border-[#E5E5E0] p-8 md:p-10 relative"
                onClick={(e) => e.stopPropagation()}
                data-testid="cookie-settings"
              >
                <button
                  onClick={closeSettings}
                  className="absolute top-4 right-4 w-9 h-9 rounded-full border border-black/10 flex items-center justify-center hover:border-[#C9A961]"
                  aria-label="Schliessen"
                >
                  <X size={16} />
                </button>
                <div className="font-mono-eyebrow text-[#C9A961] mb-3">Cookie-Einstellungen</div>
                <h2 className="font-serif-editorial text-3xl md:text-4xl leading-tight">Ihre <em className="italic text-[#C9A961]">Wahl</em>.</h2>

                <div className="mt-8 space-y-4">
                  {/* Essential */}
                  <div className="border border-[#E5E5E0] p-5 bg-white">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2">
                          <ShieldCheck size={14} className="text-[#C9A961]" />
                          <span className="font-medium text-[15px]">Technisch notwendig</span>
                        </div>
                        <p className="mt-1.5 text-[13px] text-[#0A0A0A]/70 leading-relaxed">
                          Für den Betrieb der Website erforderlich (Session, Sicherheit). Werden immer geladen.
                        </p>
                      </div>
                      <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-[#0A0A0A]/50">Immer aktiv</div>
                    </div>
                  </div>

                  {/* Marketing */}
                  <label className="block border border-[#E5E5E0] p-5 bg-white cursor-pointer hover:border-[#C9A961]/50 transition-colors" data-testid="cookie-toggle-marketing">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="font-medium text-[15px]">Marketing · Meta Pixel</div>
                        <p className="mt-1.5 text-[13px] text-[#0A0A0A]/70 leading-relaxed">
                          Wir messen mit Meta Pixel (Facebook / Instagram), welche Anzeigen zu Anfragen führen. Ihre Daten werden pseudonymisiert an Meta übermittelt.
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={state.marketing}
                        onChange={(e) => setState((s) => ({ ...s, marketing: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <span className="relative flex-shrink-0 w-11 h-6 rounded-full bg-[#0A0A0A]/10 peer-checked:bg-[#C9A961] transition-colors before:content-[''] before:absolute before:top-0.5 before:left-0.5 before:w-5 before:h-5 before:rounded-full before:bg-white before:transition-transform peer-checked:before:translate-x-5" />
                    </div>
                  </label>
                </div>

                <div className="mt-8 flex flex-wrap gap-2 justify-end">
                  <button onClick={rejectAll} className="btn-ghost !py-2.5 !px-4 text-[12.5px]" data-testid="cookie-modal-reject">
                    Alle ablehnen
                  </button>
                  <button onClick={savePrefs} className="btn-primary !py-2.5 !px-4 text-[12.5px]" data-testid="cookie-modal-save">
                    Auswahl speichern
                  </button>
                  <button onClick={acceptAll} className="btn-gold !py-2.5 !px-4 text-[12.5px]" data-testid="cookie-modal-accept-all">
                    Alle akzeptieren
                  </button>
                </div>

                <p className="mt-6 text-[11px] text-[#0A0A0A]/50">
                  Sie können Ihre Zustimmung jederzeit widerrufen. Details in der <Link to="/datenschutz" className="underline hover:text-[#C9A961]">Datenschutzerklärung</Link>.
                </p>
              </motion.div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
}
